import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { DateTime } from 'luxon';
import type { UpdateResult } from 'mongodb';
import { catchError, from, map, of, switchMap } from 'rxjs';
import {
  type LogistikAdvertisingMaterialCategory,
  type LogistikLearningMaterialCategory,
  LogistikProductAvailability,
  LogistikProductTargetAudience,
  LogistikSchoolGrade,
  LogistikShippingStatus,
  type Services,
} from './enums/LogistikEnums.js';
import { type ChancenBriefVariableValue, LogistikOrder } from './LogistikOrder.js';
import { LogistikProductFilter } from './LogistikProductFilter.js';
import { productQuantityWarning } from './mailer/logistikMailTemplates.js';
import { chancenWerkMailer } from './mailer/server/mailer.js';

/**
 * Fallback for {@link LogistikProduct.quantityWarningMail}. Products that never got an address
 * would otherwise hand `null` to nodemailer, which rejects the envelope with "No recipients
 * defined" — the low stock warning was then silently lost.
 */
export const DEFAULT_QUANTITY_WARNING_MAIL = 'chancenlogistik@chancenwerk.org';

export type LogistikProductUnion =
  | LogistikProduct
  | LearningMaterial
  | LogistikProductSet
  | AdvertisingMaterial
  | WorkMaterial
  | EventTicket
  | WorkshopPack
  | Furnishing
  | TravelCertificate;

/**
 * @entity LogistikProduct
 * @extends MongoEntity
 */
@Entity()
export class LogistikProduct extends MongoEntity {
  public static override _class = 'HubProduct'; // TODO needs migration before we can change this
  public static override _collectionName = 'hubProducts'; // TODO needs migration before we can change this

  static override async afterUpdate(
    _oldEntity: { _id?: string | undefined; _class?: string | undefined },
    newEntity: LogistikProduct,
    _result: UpdateResult,
  ): Promise<void> {
    const col = await getCollection<LogistikProduct>(LogistikProduct._class);
    await col.updateOne({ _id: newEntity._id }, { $set: { updatedAt: DateTime.now().toJSDate() } });
  }

  @Field(() => DateTime, { default: v => v ?? DateTime.now() })
  public createdAt!: DateTime;

  @Field(() => DateTime, { default: v => v ?? DateTime.now() })
  public updatedAt!: DateTime;

  @validate(({ value, key, source }) => {
    if (!value) throw new ValidationError(`Bitte geben Sie einen Titel an`, { key, source });
  })
  @Field(() => String)
  public title!: string;

  @Field(() => String)
  public description!: string;

  @Field(() => String)
  public imageID!: string;

  @Field(() => String)
  public notice: Maybe<string>;

  @Field(() => [String])
  public imageIDs?: string[];

  @Field(() => Int, { default: v => v ?? 1000 })
  public availableQuantity!: number;

  @Field(() => Int, {
    default: v => v ?? 0,
    resolve: function (this: LogistikProduct) {
      return async () => {
        const orderCol = await getRawCollection<LogistikOrder>(LogistikOrder._collectionName);
        return orderCol
          .aggregate([
            {
              $match: {
                'items.productID': this._id,
                status: { $in: [LogistikShippingStatus.COMPLETED, LogistikShippingStatus.SHIPPED] },
              },
            },
            { $unwind: '$items' },
            { $match: { 'items.productID': this._id } },
            { $group: { _id: null, quantity: { $sum: '$items.quantity' } } },
          ])
          .toArray()
          .then(result => result[0]?.quantity ?? 0);
      };
    },
  })
  public orderedQuantity!: number;

  @Field(() => Int, { default: v => v ?? 0 })
  public quantityWarningThreshold!: number;

  @validate(({ value, key, source, target }) => {
    const mail = typeof value === 'string' ? value.trim() : value;
    if (mail && !emailRegexCheck(mail as string))
      throw new ValidationError(`${mail as string} ist keine gültige E-Mail-Adresse.`, { key, source });
    // only demand an address once a threshold is set — and only if this entity actually carries both
    // fields, so partial selections (e.g. the stock deduction in LogistikOrder.afterUpdate) still save.
    if (
      !mail &&
      target.has('quantityWarningMail') &&
      target.has('quantityWarningThreshold') &&
      target.quantityWarningThreshold > 0
    )
      throw new ValidationError('Bitte geben Sie eine E-Mail-Adresse für die Bestandswarnung an.', { key, source });
  })
  @Field(() => String)
  public quantityWarningMail!: string;

  @Field(() => String)
  public productId!: string;

  @Field(() => String, { default: v => v ?? LogistikProductAvailability.AVAILABLE })
  public availability!: LogistikProductAvailability;

  @Field(() => Boolean, { default: v => v ?? true })
  public isPublished!: boolean;

  @Field(() => Int, { default: v => v ?? 1 })
  public minOrderAmount!: number;

  @Field(() => Int, { default: v => v ?? 100 })
  public maxOrderAmount!: number;

  @Query(() => [LogistikProduct])
  static getLogistikProductsByIDs(@Arg('ids', () => [String]) ids: string[]) {
    return async (gqlFields: BaseQLSelectionSet<LogistikProduct>) => {
      const col = await getCollection<LogistikProduct>(LogistikProduct._class);
      const items = await col.find({ _id: { $in: ids } }).toArray();
      return items;
    };
  }

  @Subscription(() => [LogistikProduct])
  static subscribeLogistikProductsPaginated(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => LogistikProductFilter) filter: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<LogistikProduct>) => {
      if (!filter) return of([] as LogistikProduct[]);
      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<LogistikProduct>(LogistikProduct._collectionName);
      return from(publishFilter(LogistikProduct._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            ...filter.getMongoOptions().sort,
            skip,
            limit,
          });
        }),
        catchError(e => {
          return of([] as LogistikProduct[]);
        }),
      );
    };
  }

  @Subscription(() => [LogistikProduct])
  static subscribeAllLogistikProductsBySearch(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => LogistikProductFilter) filter: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<LogistikProduct>) => {
      if (!filter) return of([] as LogistikProduct[]);
      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<LogistikProduct>(LogistikProduct._collectionName);
      return from(publishFilter(LogistikProduct._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            projection: { _id: 1 },
          });
        }),
        switchMap(async resultIDs => {
          const col = await getRawCollection<LogistikProduct>(LogistikProduct._collectionName);
          return col
            .aggregate([
              { $match: { _id: { $in: resultIDs.map(x => x._id) } } },

              // get orderedQuantity to enable sorting by it
              {
                $lookup: {
                  from: 'hubOrders',
                  as: 'orderedQuantity',
                  let: { productID: '$_id' },
                  pipeline: [
                    {
                      $match: {
                        $expr: {
                          $and: [
                            { $in: ['$$productID', '$items.productID'] },
                            { $in: ['$status', [LogistikShippingStatus.COMPLETED, LogistikShippingStatus.SHIPPED]] },
                          ],
                        },
                      },
                    },
                    { $unwind: '$items' },
                    { $match: { $expr: { $eq: ['$$productID', '$items.productID'] } } },
                    { $group: { _id: null, quantity: { $sum: '$items.quantity' } } },
                  ],
                },
              },
              {
                $set: {
                  orderedQuantity: {
                    // biome-ignore lint/suspicious/noThenProperty: bro thats just mongo shut up
                    $cond: { if: { $isArray: '$orderedQuantity' }, then: '$orderedQuantity', else: [{ quantity: 0 }] },
                  },
                },
              },
              {
                $set: {
                  orderedQuantity: {
                    // biome-ignore lint/suspicious/noThenProperty: bro thats just mongo shut up
                    $cond: { if: { $size: '$orderedQuantity' }, then: '$orderedQuantity', else: [{ quantity: 0 }] },
                  },
                },
              },
              { $unwind: '$orderedQuantity' },
              { $set: { orderedQuantity: '$orderedQuantity.quantity' } },

              // sorting from filter
              { $sort: filter.getMongoOptions().sort },
              { $skip: skip },
              { $limit: limit },
            ])
            .toArray();
        }),
        catchError(e => {
          return of([] as LogistikProduct[]);
        }),
      );
    };
  }

  @Query(() => Boolean)
  public static sendQuantityWarningMail(@Arg('productID', () => String) productID: string) {
    return async (gqlFields: void) => {
      const col = await getCollection<LogistikProduct>(LogistikProduct._class);
      const product = constructValue(await col.findOne({ _id: productID })) as Maybe<LogistikProduct>;
      if (!product) return false;

      try {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: `Warnung: Produkt ${product.title} hat nur noch ${product.availableQuantity} Stück auf Lager`,
            html: productQuantityWarning({ product }),
          }),
          new Recipient(product.quantityWarningMail?.trim() || DEFAULT_QUANTITY_WARNING_MAIL),
        );
      } catch (e) {
        console.error(e);
      }

      return true;
    };
  }

  @Subscription(() => [LogistikProduct])
  static subscribeAllPublishedLogistikProducts() {
    return (gqlFields: BaseQLSelectionSet<LogistikProduct>) => {
      const handle = getCollectionHandle<LogistikProduct>(LogistikProduct._collectionName);
      return from(publishFilter(LogistikProduct._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(
            publishFilter
              ? { $and: [publishFilter, { isPublished: { $ne: false } }, { _class: LogistikProduct._class }] }
              : { $and: [{ isPublished: { $ne: false } }, { _class: LogistikProduct._class }] },
            { sort: { _id: -1 } },
          );
        }),
        catchError(e => {
          return of([] as LogistikProduct[]);
        }),
      );
    };
  }
}

/**
 * @entity LearningMaterial
 * @extends LogistikProduct
 */
@Entity()
export class LearningMaterial extends LogistikProduct {
  public static override _class = 'LearningMaterial';

  @Field(() => String)
  public pdfID!: string;

  @Field(() => String)
  public solutionPDFID: Maybe<string>;

  @Field(() => String)
  public handoutPDFID: Maybe<string>;

  @validate(({ value, key, source }) => {
    if (!value) throw new ValidationError(`Bitte geben Sie ein Thema an`, { key, source });
  })
  @Field(() => String)
  public topic!: string;

  @validate(({ value, key, source }) => {
    if (!value) throw new ValidationError(`Bitte geben Sie ein Fach an`, { key, source });
  })
  @Field(() => String)
  public category!: LogistikLearningMaterialCategory;

  @validate(({ value, key, source }) => {
    if (!value || value.length === 0)
      throw new ValidationError(`Bitte geben Sie mindestens eine Jahrgangsstufe an`, { key, source });
    if (!value.every(v => Object.values(LogistikSchoolGrade).includes(v)))
      throw new ValidationError(`Ungültige Jahrgangsstufe`, { key, source });
  })
  @Field(() => [String])
  public grades!: LogistikSchoolGrade[];

  @Field(() => String, { default: v => v ?? null })
  public digitalVariantPDFID: Maybe<string>;
}

@Entity()
export class LogistikProductSet extends LogistikProduct {
  public static override _class = 'LogistikProductSet';

  @validate(({ value, key, source }) => {
    if (!value?.length) throw new ValidationError('Bitte wählen Sie mindestens ein Produkt aus.');
  })
  @Field(() => [SelectedSetProduct], { default: v => v ?? [] })
  public selectedProducts!: SelectedSetProduct[];

  @Field(() => [String], { default: v => v ?? [] })
  public services!: Services[];
}

@Entity()
export class SelectedSetProduct extends AdornisEntity {
  public static override _class = 'SelectedSetProduct';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  @Field(() => String)
  public productID!: string;

  @validate(({ value, key, source }) => {
    if (!value) throw new ValidationError(`Bitte geben Sie eine Anzahl an`, { key, source });
    if (value < 1) throw new ValidationError(`Anzahl muss mindestens 1 sein`, { key, source });
  })
  @Field(() => Int, { default: v => v ?? 1 })
  public quantity!: number;
}

/**
 * @entity AdvertisingMaterial
 * @extends LogistikProduct
 */
@Entity()
export class AdvertisingMaterial extends LogistikProduct {
  public static override _class = 'AdvertisingMaterial';

  // TODO would be nicer to use some kind of trait system or multiple inheritance to get this field into chancenbrief and advertisingMatieral
  @validate(({ value, key, source }: ValidationFunctionOptions<Maybe<string[]>, AdvertisingMaterial>) => {
    if (!value)
      throw new ValidationError(`Bitte geben Sie eine Zielgruppe an oder wählen Sie "Alle" aus`, { key, source });
    for (const valueEntry of value) {
      if (!Object.values(LogistikProductTargetAudience).includes(valueEntry as LogistikProductTargetAudience))
        throw new ValidationError(
          `Es können nur vordefinierte Werte für die Zielgrupppe genutzt werden! (gegeben wurde: ${valueEntry})`,
        );
    }
  })
  @Field(() => [String])
  targetAudiences?: Maybe<LogistikProductTargetAudience[]>;

  @Field(() => String)
  public pdfID?: string;

  @Field(() => String)
  public category!: LogistikAdvertisingMaterialCategory;
}

@Entity()
export class WorkMaterial extends LogistikProduct {
  static override _class = 'WorkMaterial';

  @Subscription(() => [WorkMaterial])
  static subscribePublishedWorkMaterials(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => LogistikProductFilter) filter?: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<WorkMaterial>) => {
      const selector = filter?.getMongoFilter() ?? {};
      const handle = getCollectionHandle<WorkMaterial>(LogistikProduct._collectionName);
      return from(publishFilter(LogistikProduct._class)).pipe(
        switchMap(pF =>
          handle.watchQuery({
            $and: [
              { _class: { $in: LogistikProduct.validSubclasses() } },
              { isPublished: { $ne: false } },
              selector,
              ...(pF ? [pF] : []),
            ],
          }),
        ),
      );
    };
  }

  @Subscription(() => Int)
  static subscribePublishedWorkMaterialsCount() {
    return () => {
      const handle = getCollectionHandle<WorkMaterial>(LogistikProduct._collectionName);
      return handle
        .watchQuery(
          { _class: { $in: LogistikProduct.validSubclasses() }, isPublished: { $ne: false } },
          { projection: { _id: 1 } },
        )
        .pipe(map(furnishings => furnishings.length));
    };
  }
}

@Entity()
export class EventTicket extends LogistikProduct {
  static override _class = 'EventTicket';

  @Subscription(() => [EventTicket])
  static subscribePublishedEventTickets(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => LogistikProductFilter) filter?: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<EventTicket>) => {
      const selector = filter?.getMongoFilter() ?? {};
      const handle = getCollectionHandle<EventTicket>(LogistikProduct._collectionName);
      return from(publishFilter(LogistikProduct._class)).pipe(
        switchMap(pF =>
          handle.watchQuery({
            $and: [
              { _class: { $in: LogistikProduct.validSubclasses() } },
              { isPublished: { $ne: false } },
              selector,
              ...(pF ? [pF] : []),
            ],
          }),
        ),
      );
    };
  }

  @Subscription(() => Int)
  static subscribePublishedEventTicketsCount() {
    return () => {
      const handle = getCollectionHandle<EventTicket>(LogistikProduct._collectionName);
      return handle
        .watchQuery(
          { _class: { $in: LogistikProduct.validSubclasses() }, isPublished: { $ne: false } },
          { projection: { _id: 1 } },
        )
        .pipe(map(tickets => tickets.length));
    };
  }
}

@Entity()
export class WorkshopPack extends LogistikProduct {
  static override _class = 'WorkshopPack';

  @Subscription(() => [EventTicket])
  static subscribePublishedWorkshopPack(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => LogistikProductFilter) filter?: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<WorkshopPack>) => {
      const selector = filter?.getMongoFilter() ?? {};
      const handle = getCollectionHandle<WorkshopPack>(LogistikProduct._collectionName);
      return from(publishFilter(LogistikProduct._class)).pipe(
        switchMap(pF =>
          handle.watchQuery({
            $and: [
              { _class: { $in: LogistikProduct.validSubclasses() } },
              { isPublished: { $ne: false } },
              selector,
              ...(pF ? [pF] : []),
            ],
          }),
        ),
      );
    };
  }

  @Subscription(() => Int)
  static subscribePublishedWorkshopPackCount() {
    return () => {
      const handle = getCollectionHandle<WorkshopPack>(LogistikProduct._collectionName);
      return handle
        .watchQuery(
          { _class: { $in: LogistikProduct.validSubclasses() }, isPublished: { $ne: false } },
          { projection: { _id: 1 } },
        )
        .pipe(map(tickets => tickets.length));
    };
  }
}

@Entity()
export class Furnishing extends LogistikProduct {
  static override _class = 'Furnishing';

  @Subscription(() => [Furnishing])
  static subscribePublishedFurnishings(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => LogistikProductFilter) filter?: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<Furnishing>) => {
      const selector = filter?.getMongoFilter();
      const handle = getCollectionHandle<Furnishing>(LogistikProduct._collectionName);
      return from(publishFilter(LogistikProduct._class)).pipe(
        switchMap(pF =>
          handle.watchQuery({
            $and: [
              { _class: { $in: LogistikProduct.validSubclasses() } },
              { isPublished: { $ne: false } },
              selector,
              ...(pF ? [pF] : []),
            ],
          }),
        ),
      );
    };
  }

  @Subscription(() => Int)
  static subscribePublishedFurnishingsCount() {
    return () => {
      const handle = getCollectionHandle<Furnishing>(LogistikProduct._collectionName);
      return handle
        .watchQuery(
          { _class: { $in: LogistikProduct.validSubclasses() }, isPublished: { $ne: false } },
          { projection: { _id: 1 } },
        )
        .pipe(map(furnishings => furnishings.length));
    };
  }
}

export const possiblePrefillChancenBriefVariableNames = [
  'Schule_Name',
  'Schule_Strasse',
  'Schule_Hausnummer',
  'Schule_PLZ',
  'Schule_Stadt',
  'Schule_Kuerzel',
  'Wochentag_1',
  'Wochentag_2',
  'Wochentag_3',
  'Lernförderzeit_1',
  'Lernförderzeit_2',
  'Lernförderzeit_3',
  'Raumnummer',
  'Name_Schulkoordination',
  'Mobilnummer',
  'email1',
  'email2',
  'Name',
] as const;

export enum DocxVariableType {
  TEXT = 'Text',
  QR_CODE = 'QR-Code',
}

@Entity()
export class ChancenBriefVariable extends AdornisEntity {
  static override _class = 'FLYAVariable'; // TODO need to add migration to update this

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @validate(nonOptional())
  @Field(() => String)
  variableName!: string;

  @Field(() => String, { default: v => v ?? DocxVariableType.TEXT })
  type!: DocxVariableType;

  @Field(() => Int, { default: v => v ?? 150 })
  width!: number;

  @Field(() => Int, { default: v => v ?? 150 })
  height!: number;
}

@Entity()
export class TravelCertificate extends LogistikProduct {
  static override _class = 'TravelCertificate';

  @validate(({ value, key, source }: ValidationFunctionOptions<Maybe<string[]>, ChancenBrief>) => {
    if (!value)
      throw new ValidationError(`Bitte geben Sie eine Zielgruppe an oder wählen Sie "Alle" aus`, { key, source });
    for (const valueEntry of value) {
      if (!Object.values(LogistikProductTargetAudience).includes(valueEntry as LogistikProductTargetAudience))
        throw new ValidationError(
          `Es können nur vordefinierte Werte für die Zielgrupppe genutzt werden! (gegeben wurde: ${valueEntry})`,
        );
    }
  })
  @Field(() => [String])
  targetAudiences?: Maybe<LogistikProductTargetAudience[]>;

  @validate(nonOptional())
  @Field(() => ID)
  flyaDocumentID!: string;

  @Field(() => [ChancenBriefVariable], { default: v => v ?? [] })
  flyaVariables: Maybe<ChancenBriefVariable[]>;

  getGeneratedPDFLink(chancenBriefVariableValues: ChancenBriefVariableValue[]) {
    return {
      path: `/api/serve-travel-certificate/${this._id}/${encodeURIComponent(`${this.title}.pdf`)}`,
      searchParams: new URLSearchParams({
        variables: JSON.stringify(chancenBriefVariableValues.map(variable => variable.toJSON())),
      }),
    };
  }
}

@Entity()
export class ChancenBrief extends LogistikProduct {
  static override _class = 'FLYA'; // TODO need to add migration to update this

  // TODO would be nicer to use some kind of trait system or multiple inheritance to get this field into chancenbrief and advertisingMatieral
  @validate(({ value, key, source }: ValidationFunctionOptions<Maybe<string[]>, ChancenBrief>) => {
    if (!value)
      throw new ValidationError(`Bitte geben Sie eine Zielgruppe an oder wählen Sie "Alle" aus`, { key, source });
    for (const valueEntry of value) {
      if (!Object.values(LogistikProductTargetAudience).includes(valueEntry as LogistikProductTargetAudience))
        throw new ValidationError(
          `Es können nur vordefinierte Werte für die Zielgrupppe genutzt werden! (gegeben wurde: ${valueEntry})`,
        );
    }
  })
  @Field(() => [String])
  targetAudiences?: Maybe<LogistikProductTargetAudience[]>;

  @validate(nonOptional())
  @Field(() => ID)
  flyaDocumentID!: string;

  @Field(() => [ChancenBriefVariable], { default: v => v ?? [] })
  flyaVariables: Maybe<ChancenBriefVariable[]>;

  getGeneratedPDFLink(chancenBriefVariableValues: ChancenBriefVariableValue[]) {
    return {
      path: `/api/serve-chancenbrief/${this._id}/${encodeURIComponent(`${this.title}.pdf`)}`,
      searchParams: new URLSearchParams({
        variables: JSON.stringify(chancenBriefVariableValues.map(variable => variable.toJSON())),
      }),
    };
  }

  @Subscription(() => [ChancenBrief])
  static subscribePublishedChancenBriefe(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => LogistikProductFilter) filter?: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<ChancenBrief>) => {
      const selector = filter?.getMongoFilter() ?? {};
      const handle = getCollectionHandle<ChancenBrief>(LogistikProduct._collectionName);
      return from(publishFilter(LogistikProduct._class)).pipe(
        switchMap(pF =>
          handle.watchQuery({
            $and: [
              { _class: { $in: LogistikProduct.validSubclasses() } },
              { isPublished: { $ne: false } },
              selector,
              ...(pF ? [pF] : []),
            ],
          }),
        ),
      );
    };
  }

  @Subscription(() => Int)
  static subscribePublishedChancenBriefeCount() {
    return () => {
      const handle = getCollectionHandle<Furnishing>(LogistikProduct._collectionName);
      return handle
        .watchQuery(
          { _class: { $in: LogistikProduct.validSubclasses() }, isPublished: { $ne: false } },
          { projection: { _id: 1 } },
        )
        .pipe(map(furnishings => furnishings.length));
    };
  }
}

export const g_getProductCategoryString = (category: string) => {
  switch (category) {
    case LearningMaterial._class:
    case 'LearningMaterial':
      return 'CHANCENheft';
    case AdvertisingMaterial._class:
    case 'AdvertisingMaterial':
      return 'Werbe- und Akquisematerial';
    case WorkMaterial._class:
    case 'WorkMaterial':
      return 'Büromaterial';
    case EventTicket._class:
    case 'EventTicket':
      return 'Eintrittskarten';
    case Furnishing._class:
    case 'Furnishing':
      return 'Mobiliar';
    case WorkshopPack._class:
      return 'CHANCENakademie Workshop-Pakete';
    case ChancenBrief._class:
      return 'CHANCENbrief';
    case LogistikProductSet._class:
      return 'CHANCENset';
    case TravelCertificate._class:
      return 'CHANCENreise-Zertifikat';
    default:
      return 'unbekannt';
  }
};
